import { SearchContentType } from '@novaheal/types';
import type { Dispatch, SetStateAction } from 'react';

/** Algolia facet string + UI label (German copy when it differs from `value`). */
export type ArticleSearchFacetCatalogEntry = {
  value: string;
  label: string;
};

export const ARTICLE_SEARCH_CE_FACET_CATALOG: readonly ArticleSearchFacetCatalogEntry[] = [
  { value: 'CE01', label: 'CE01' },
  { value: 'CE02', label: 'CE02' },
  { value: 'CE03', label: 'CE03' },
  { value: 'CE04', label: 'CE04' },
  { value: 'CE05', label: 'CE05' },
  { value: 'CE06', label: 'CE06' },
  { value: 'CE07', label: 'CE07' },
  { value: 'CE08', label: 'CE08' },
  { value: 'CE09', label: 'CE09' },
  { value: 'CE10', label: 'CE10' },
  { value: 'CE11', label: 'CE11' },
];

export const ARTICLE_SEARCH_KB_FACET_CATALOG: readonly ArticleSearchFacetCatalogEntry[] = [
  { value: 'KB I', label: 'KB I' },
  { value: 'KB II', label: 'KB II' },
  { value: 'KB III', label: 'KB III' },
  { value: 'KB IV', label: 'KB IV' },
  { value: 'KB V', label: 'KB V' },
];

/** Registered search filter groups (extend when new Algolia facets ship). */
export type SearchFilterCategoryId = 'ce' | 'kb';

/** Algolia `attributesForFaceting` keys used for article tag filters — single source of truth. */
export const SEARCH_FILTER_FACET_KEYS = {
  ce: 'fields.tags.ce',
  kb: 'fields.tags.kb',
} as const;

export type SearchFilterFacetAttribute =
  (typeof SEARCH_FILTER_FACET_KEYS)[keyof typeof SEARCH_FILTER_FACET_KEYS];

export type SearchFilterFacetOption = {
  value: string;
  label: string;
};

/** Wired from the parent that owns CE/KB tag state (e.g. SearchResultList). */
export type SearchFilterSelectionContext = {
  selectedCeTags: string[];
  selectedKbTags: string[];
  setSelectedCeTags: Dispatch<SetStateAction<string[]>>;
  setSelectedKbTags: Dispatch<SetStateAction<string[]>>;
};

export type SearchFilterCategoryDef = {
  id: SearchFilterCategoryId;
  facetAttribute: SearchFilterFacetAttribute;
  label: string;
  getSelected: (ctx: SearchFilterSelectionContext) => string[];
  setSelected: (ctx: SearchFilterSelectionContext, next: string[]) => void;
  facetCatalog: readonly ArticleSearchFacetCatalogEntry[];
};

/**
 * Builds chip options from the catalog provided
 */
export const buildFacetOptions = (
  category: Pick<SearchFilterCategoryDef, 'facetCatalog'>
): SearchFilterFacetOption[] => {
  return category.facetCatalog.map((facet) => ({
    value: facet.value,
    label: facet.label,
  }));
};

/** Ordered list — UI and facet-filter builders should iterate this instead of hard-coding CE/KB. */
export const SEARCH_FILTER_CATEGORIES: readonly SearchFilterCategoryDef[] = [
  {
    id: 'ce',
    facetAttribute: SEARCH_FILTER_FACET_KEYS.ce,
    label: 'Curriculum-Einheiten (CE)',
    facetCatalog: ARTICLE_SEARCH_CE_FACET_CATALOG,
    getSelected: (ctx) => ctx.selectedCeTags,
    setSelected: (ctx, next) => {
      ctx.setSelectedCeTags(next);
    },
  },
  {
    id: 'kb',
    facetAttribute: SEARCH_FILTER_FACET_KEYS.kb,
    label: 'Kompetenzbereiche (KB)',
    facetCatalog: ARTICLE_SEARCH_KB_FACET_CATALOG,
    getSelected: (ctx) => ctx.selectedKbTags,
    setSelected: (ctx, next) => {
      ctx.setSelectedKbTags(next);
    },
  },
];

/**
 * True when the current search includes articles.
 * The filter trigger is only shown for ARTICLE search results.
 */
export const shouldShowSearchFilterTrigger = (contentTypes: SearchContentType[]): boolean =>
  contentTypes.includes(SearchContentType.ARTICLE);
